import React from 'react';
import './PrivacyPolicy.css';

const PrivacyPolicy = () => {
    return (
        <div className="privacy-page">
            <div className="privacy-container">

                {/* Header */}
                <div className="privacy-header">

                    <h1>Privacy Policy</h1>

                    <p className="privacy-header-description">
                        Your privacy matters to us. Learn how Value Loads
                        collects, uses, protects, and manages your information.
                    </p>
                </div>

                {/* Introduction */}
                <div className="privacy-intro">
                    <div className="intro-icon">
                        🔒
                    </div>

                    <div>
                        <h3>Your Privacy Is Important</h3>

                        <p>
                            This Privacy Policy explains how we collect, use,
                            share, and protect your personal information when
                            you visit our website at <strong>valueloads.com</strong>,
                            use our services, or interact with us in any way.
                            By using our platform, you agree to the practices
                            described in this policy. If you don't agree,
                            please discontinue use of our services and reach
                            out to us with any concerns.
                        </p>
                    </div>
                </div>

                {/* Content */}
                <div className="privacy-content">

                    <section className="privacy-section">
                        <div className="section-number">01</div>

                        <div className="section-content">
                            <h2>Who We Are</h2>

                            <p>
                                Value Logistics Inc., operating as Value Loads,
                                is a third-party logistics company serving all
                                48 contiguous states and Canada. Our corporate
                                address is 26133 US HWY 19 N, Suite 302,
                                Clearwater, FL 33763.
                            </p>

                            <p>
                                We connect shippers and carriers, offering
                                freight brokerage, land transport, smart
                                warehousing, drayage, and supply chain solutions.
                            </p>
                        </div>
                    </section>

                    <section className="privacy-section">
                        <div className="section-number">02</div>

                        <div className="section-content">
                            <h2>Information We Collect</h2>

                            <p>
                                We collect information in a few different ways
                                depending on how you interact with us.
                            </p>

                            <div className="info-card">
                                <h3>Information you give us directly</h3>

                                <ul>
                                    <li>
                                        Your name, email address, phone number,
                                        and company details when you fill out a
                                        contact form, request a quote, or sign
                                        up as a shipper or carrier.
                                    </li>

                                    <li>
                                        Payment and billing information when you
                                        use our services.
                                    </li>

                                    <li>
                                        Any documents, communications, or files
                                        you share with us during the course of
                                        doing business.
                                    </li>

                                    <li>
                                        Messages, feedback, or support requests
                                        you send our way.
                                    </li>
                                </ul>
                            </div>

                            <div className="info-card">
                                <h3>Information collected automatically</h3>

                                <ul>
                                    <li>
                                        IP address, browser type, and device
                                        information.
                                    </li>

                                    <li>
                                        Pages visited on our website, time spent,
                                        and click behavior.
                                    </li>

                                    <li>
                                        Referral sources that brought you to
                                        our site.
                                    </li>

                                    <li>
                                        Cookies and similar tracking technologies.
                                    </li>
                                </ul>
                            </div>
                        </div>
                    </section>

                    <section className="privacy-section">
                        <div className="section-number">03</div>

                        <div className="section-content">
                            <h2>Location Information</h2>

                            <p>
                                Our website may collect and process your device's
                                location data to provide location-based services,
                                improve user experience, and enable features such
                                as nearby services, navigation, or location-specific
                                content.
                            </p>

                            <div className="highlight-box">
                                <strong>Location Permission</strong>

                                <p>
                                    Location data is collected <strong>only with
                                    your explicit permission</strong> and is used
                                    solely for the purposes described in this policy.
                                </p>
                            </div>

                            <p>Specifically, we may use location data to:</p>

                            <ul>
                                <li>
                                    Match you with carriers or shippers operating
                                    in your geographic area.
                                </li>

                                <li>
                                    Provide route and navigation assistance
                                    relevant to freight or pickup locations.
                                </li>

                                <li>
                                    Display location-specific services, pricing,
                                    or availability.
                                </li>

                                <li>
                                    Improve the accuracy and reliability of our
                                    logistics tools.
                                </li>
                            </ul>

                            <p>
                                You can withdraw your location permission at any
                                time through your device or browser settings.
                                Please note that doing so may limit the functionality
                                of certain features within our platform.
                            </p>

                            <p>
                                We do not sell or share your location data with
                                third parties for advertising purposes.
                            </p>
                        </div>
                    </section>

                    <section className="privacy-section">
                        <div className="section-number">04</div>

                        <div className="section-content">
                            <h2>How We Use Your Information</h2>

                            <p>
                                We use the information we collect to run our
                                business and serve you better. That includes:
                            </p>

                            <ul>
                                <li>Processing and managing freight bookings, shipments, and quotes.</li>
                                <li>Communicating with you about your shipments, account, or inquiries.</li>
                                <li>Verifying carrier and shipper credentials through our vetting process.</li>
                                <li>Sending relevant updates, industry news, or service announcements.</li>
                                <li>Improving our website, tools, and overall service quality.</li>
                                <li>Preventing fraud, unauthorized access, and other security concerns.</li>
                                <li>Complying with legal and regulatory obligations.</li>
                            </ul>

                            <p>
                                We will never use your information for purposes
                                unrelated to the above without your knowledge
                                and consent, where required by law.
                            </p>
                        </div>
                    </section>

                    <section className="privacy-section">
                        <div className="section-number">05</div>

                        <div className="section-content">
                            <h2>Sharing Your Information</h2>

                            <p>
                                We don't sell your personal data. However, there
                                are some situations where we do share information
                                with others:
                            </p>

                            <div className="sharing-grid">
                                <div className="sharing-card">
                                    <h3>Carriers and Shippers</h3>
                                    <p>
                                        To facilitate the logistics transactions
                                        you've requested, relevant contact and
                                        shipment details may be shared between
                                        the involved parties.
                                    </p>
                                </div>

                                <div className="sharing-card">
                                    <h3>Service Providers</h3>
                                    <p>
                                        We work with trusted third-party vendors,
                                        such as IT providers, analytics tools,
                                        and payment processors, who help us
                                        operate our business.
                                    </p>
                                </div>

                                <div className="sharing-card">
                                    <h3>Legal Compliance</h3>
                                    <p>
                                        If we're legally required to disclose
                                        information, such as in response to a
                                        valid court order or government request,
                                        we will do so.
                                    </p>
                                </div>

                                <div className="sharing-card">
                                    <h3>Business Transfers</h3>
                                    <p>
                                        In the unlikely event of a merger,
                                        acquisition, or sale of our business,
                                        your data may be transferred as part
                                        of that transaction.
                                    </p>
                                </div>
                            </div>
                        </div>
                    </section>

                    <section className="privacy-section">
                        <div className="section-number">06</div>

                        <div className="section-content">
                            <h2>Cookies and Tracking</h2>

                            <p>
                                Like most websites, valueloads.com uses cookies,
                                small text files stored on your device to make
                                your experience smoother and to understand how
                                people use our site.
                            </p>

                            <p>
                                We use them for things like remembering your
                                preferences, keeping you logged in, and analyzing
                                traffic through tools like Google Analytics and
                                Google Tag Manager.
                            </p>

                            <p>
                                You can control cookie behavior through your
                                browser settings. Disabling certain cookies may
                                affect how parts of the website function, but it
                                won't prevent you from using the core features.
                            </p>
                        </div>
                    </section>

                    <section className="privacy-section">
                        <div className="section-number">07</div>

                        <div className="section-content">
                            <h2>Data Security</h2>

                            <p>
                                We take reasonable technical and organizational
                                steps to protect your personal information from
                                unauthorized access, misuse, alteration, or loss.
                                Our systems are built with security in mind, and
                                we regularly review our practices to keep your
                                data safe.
                            </p>

                            <p>
                                That said, no method of data transmission or
                                storage over the internet is 100% foolproof.
                                If you ever suspect unauthorized use of your
                                account or data, contact us immediately at
                                <a href="mailto:vl@valueloads.com">
                                    vl@valueloads.com
                                </a>.
                            </p>

                            <div className="fraud-alert">
                                <div className="fraud-icon">⚠</div>

                                <div>
                                    <h3>Important Fraud Notice</h3>

                                    <p>
                                        Value Logistics Inc. communicates only
                                        through official email domains. We will
                                        never contact you from Gmail, Yahoo,
                                        Hotmail, or any free public email domain.
                                        If you receive suspicious communication,
                                        please report it to us right away.
                                    </p>
                                </div>
                            </div>
                        </div>
                    </section>

                    <section className="privacy-section">
                        <div className="section-number">08</div>

                        <div className="section-content">
                            <h2>Data Retention</h2>

                            <p>
                                We hold onto your personal information for as
                                long as it's necessary to deliver our services
                                and meet our legal, accounting, and compliance
                                obligations.
                            </p>

                            <p>
                                When we no longer have a legitimate reason to
                                keep your data, we'll securely delete or
                                anonymize it.
                            </p>
                        </div>
                    </section>

                    <section className="privacy-section">
                        <div className="section-number">09</div>

                        <div className="section-content">
                            <h2>Your Rights and Choices</h2>

                            <p>
                                Depending on where you're located, you may have
                                certain rights over your personal data. These
                                can include:
                            </p>

                            <div className="rights-grid">
                                <div className="right-card">
                                    <strong>Access</strong>
                                    <span>
                                        Request a copy of the personal information
                                        we hold about you.
                                    </span>
                                </div>

                                <div className="right-card">
                                    <strong>Correction</strong>
                                    <span>
                                        Ask us to fix any inaccurate or incomplete
                                        information.
                                    </span>
                                </div>

                                <div className="right-card">
                                    <strong>Deletion</strong>
                                    <span>
                                        Request that we delete your personal data,
                                        subject to legal retention requirements.
                                    </span>
                                </div>

                                <div className="right-card">
                                    <strong>Opt-Out</strong>
                                    <span>
                                        Unsubscribe from marketing emails or opt
                                        out of non-essential data collection.
                                    </span>
                                </div>

                                <div className="right-card">
                                    <strong>Data Portability</strong>
                                    <span>
                                        Request your data in a portable,
                                        machine-readable format where applicable.
                                    </span>
                                </div>
                            </div>

                            <p>
                                To exercise any of these rights, reach out to us
                                at{' '}
                                <a href="mailto:vl@valueloads.com">
                                    vl@valueloads.com
                                </a>.
                                We'll respond within a reasonable timeframe.
                            </p>
                        </div>
                    </section>

                    <section className="privacy-section">
                        <div className="section-number">10</div>

                        <div className="section-content">
                            <h2>Children's Privacy</h2>

                            <p>
                                Our services are designed for businesses and
                                professionals in the logistics industry. We do
                                not knowingly collect personal information from
                                anyone under the age of 13.
                            </p>

                            <p>
                                If we become aware that a child has provided us
                                with personal information, we will take prompt
                                steps to delete it. If you believe a child has
                                submitted information to us, please contact us
                                right away.
                            </p>
                        </div>
                    </section>

                    <section className="privacy-section">
                        <div className="section-number">11</div>

                        <div className="section-content">
                            <h2>Third-Party Links</h2>

                            <p>
                                Our website may include links to third-party
                                websites, such as industry resources, carrier
                                portals, or partner platforms.
                            </p>

                            <p>
                                We don't control those sites and are not
                                responsible for their privacy practices. We
                                recommend reviewing the privacy policies of any
                                third-party sites you visit before submitting
                                personal information.
                            </p>
                        </div>
                    </section>

                    <section className="privacy-section">
                        <div className="section-number">12</div>

                        <div className="section-content">
                            <h2>Changes to This Policy</h2>

                            <p>
                                We may update this Privacy Policy from time to
                                time as our services evolve or as laws change.
                                When we make meaningful updates, we'll revise
                                the "Last Updated" date at the top of this page.
                            </p>

                            <p>
                                For significant changes, we may also notify you
                                directly via email or through a notice on our
                                website.
                            </p>
                        </div>
                    </section>

                </div>

                {/* Contact */}
                <div className="privacy-contact">
                    <div className="contact-icon">✉</div>

                    <div>
                        <h2>Have Questions?</h2>

                        <p>
                            If you have any questions about this Privacy Policy
                            or how we handle your data, please contact us.
                        </p>

                        <div className="contact-details">
                            <a href="mailto:vl@valueloads.com">
                                vl@valueloads.com
                            </a>

                            <span>•</span>

                            <a href="tel:+16785037708">
                                +1 (678)-503-7708
                            </a>
                        </div>
                    </div>
                </div>

                {/* Footer */}
                <div className="privacy-footer">
                    <p>
                        © {new Date().getFullYear()} Value Logistics Inc.
                        All rights reserved.
                    </p>
                </div>

            </div>
        </div>
    );
};

export default PrivacyPolicy;